import { useController, useFormContext } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { Button } from '@/shared/ui/Button'
import { controlVariants } from '@/shared/ui/Form/control-styles'
import { AppIcon } from '@/shared/ui/icons'

type MissionQrCodeEvidenceControlsProps = {
  isSubmitting: boolean
  isReadonly: boolean
  isCapacityFull: boolean
  onOpenCamera: () => void
}

/**
 * Missão de evidência por código aceita as duas entradas: ler pela câmera ou
 * **digitar a palavra**. O campo existe porque nem sempre a câmera colabora —
 * luz ruim, permissão negada, QR amassado — e o código virou legível o
 * bastante para ser digitado.
 *
 * As duas entradas ficam **empilhadas**, não lado a lado: a diretriz L2 do
 * design system só admite um elemento fixo ao lado de uma linha com texto, e a
 * 375px campo mais botão não cabem na mesma linha.
 *
 * A comparação tolerante (acento, caixa, espaços) mora no backend, em
 * `QrCode#match_code` — aqui o valor sobe como o participante digitou.
 */
export const MissionQrCodeEvidenceControls = ({
  isSubmitting,
  isReadonly,
  isCapacityFull,
  onOpenCamera
}: MissionQrCodeEvidenceControlsProps) => {
  const { control } = useFormContext()
  const {
    field: { value, onChange, onBlur, ref }
  } = useController({ name: 'content', control })

  const typedCode = String(value ?? '').trim()
  const isDisabled = isSubmitting || isCapacityFull || isReadonly

  return (
    <div className="flex flex-col gap-3">
      <Button
        type="button"
        variant="primary"
        size="md"
        showTrailingIcon={false}
        className="w-full justify-center"
        disabled={isDisabled}
        onClick={onOpenCamera}
        leadingIcon={<AppIcon name="camera" />}
      >
        Abrir câmera
      </Button>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="mission-typed-code"
          className="text-base text-gray-700"
        >
          Não conseguiu escanear? Digite a palavra do código
        </label>

        <input
          id="mission-typed-code"
          ref={ref}
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="none"
          placeholder="agua-gratis"
          className={cn(controlVariants({ size: 'lg' }))}
          value={String(value ?? '')}
          onChange={onChange}
          onBlur={onBlur}
          disabled={isDisabled}
        />

        <Button
          type="submit"
          variant="success"
          size="lg"
          showTrailingIcon={false}
          className="w-full justify-center"
          disabled={isDisabled || typedCode.length === 0}
        >
          {isSubmitting ? 'Enviando...' : 'Enviar palavra'}
        </Button>
      </div>
    </div>
  )
}
